import type { FxRateQuery } from '@contexts/FxConverter/domain/FxRate.domain'
import { useGetCurrencies } from '@contexts/FxConverter/repositories/useGetCurrencies'
import { useGetFxRate } from '@contexts/FxConverter/repositories/useGetFxRate'
import { format } from 'date-fns'
import { ArrowRightLeft, TrendingUp } from 'lucide-react'
import { useState } from 'react'

const today = format(new Date(), 'yyyy-MM-dd')

export const FxConverterCard = () => {
  const [from, setFrom] = useState('USD')
  const [to, setTo] = useState('EUR')
  const [date, setDate] = useState(today)
  const [amount, setAmount] = useState('1')
  const [query, setQuery] = useState<FxRateQuery | null>(null)

  const { data: currencies, isLoading: loadingCurrencies } = useGetCurrencies()
  const { data: result, isLoading: loadingRate, isError, error } = useGetFxRate(query)

  const handleSwap = () => {
    setFrom(to)
    setTo(from)
    setQuery(null)
  }

  const handleConvert = () => {
    const parsedAmount = parseFloat(amount)
    if (!from || !to || !date || isNaN(parsedAmount) || parsedAmount <= 0) return
    setQuery({ from, to, date, amount: parsedAmount })
  }

  const currencyOptions = currencies
    ? Object.entries(currencies).sort(([a], [b]) => a.localeCompare(b))
    : []

  return (
    <div className="w-full max-w-lg">
      <div className="mb-8 text-center">
        <div className="mb-3 inline-flex items-center justify-center rounded-2xl bg-primary/10 p-3">
          <TrendingUp className="h-7 w-7 text-primary" />
        </div>
        <h1 className="text-3xl font-bold tracking-tight">FX Converter</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Historical exchange rates powered by Frankfurter
        </p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        {/* Amount */}
        <div className="mb-4">
          <label className="mb-1.5 block text-sm font-medium">Amount</label>
          <input
            type="number"
            min="0.01"
            step="any"
            value={amount}
            onChange={(e) => { setAmount(e.target.value); setQuery(null) }}
            className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none ring-ring transition focus:border-ring focus:ring-2 focus:ring-ring/20"
          />
        </div>

        {/* Currency pair */}
        <div className="mb-4 flex items-end gap-2">
          <div className="flex-1">
            <label className="mb-1.5 block text-sm font-medium">From</label>
            <select
              value={from}
              disabled={loadingCurrencies}
              onChange={(e) => { setFrom(e.target.value); setQuery(null) }}
              className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none ring-ring transition focus:border-ring focus:ring-2 focus:ring-ring/20 disabled:opacity-50"
            >
              {currencyOptions.map(([code, name]) => (
                <option key={code} value={code}>
                  {code} — {String(name)}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleSwap}
            title="Swap currencies"
            className="mb-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-background transition hover:bg-muted"
          >
            <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
          </button>

          <div className="flex-1">
            <label className="mb-1.5 block text-sm font-medium">To</label>
            <select
              value={to}
              disabled={loadingCurrencies}
              onChange={(e) => { setTo(e.target.value); setQuery(null) }}
              className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none ring-ring transition focus:border-ring focus:ring-2 focus:ring-ring/20 disabled:opacity-50"
            >
              {currencyOptions.map(([code, name]) => (
                <option key={code} value={code}>
                  {code} — {String(name)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Date */}
        <div className="mb-5">
          <label className="mb-1.5 block text-sm font-medium">Date</label>
          <input
            type="date"
            value={date}
            max={today}
            onChange={(e) => { setDate(e.target.value); setQuery(null) }}
            className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none ring-ring transition focus:border-ring focus:ring-2 focus:ring-ring/20"
          />
        </div>

        {/* Convert button */}
        <button
          onClick={handleConvert}
          disabled={loadingRate || !from || !to || !date || !amount}
          className="w-full rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loadingRate ? 'Fetching rate…' : 'Convert'}
        </button>

        {/* Result */}
        {isError && (
          <div className="mt-4 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {(error as Error)?.message ?? 'Failed to fetch exchange rate. Please try again.'}
          </div>
        )}

        {result && !loadingRate && (
          <div className="mt-4 rounded-xl bg-muted px-5 py-4 text-center">
            <p className="text-2xl font-bold">
              {result.amount.toLocaleString()} {result.from}
              <span className="mx-2 text-muted-foreground">=</span>
              <span className="text-primary">
                {result.converted.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 6,
                })}{' '}
                {result.to}
              </span>
            </p>
            <p className="mt-1.5 text-xs text-muted-foreground">
              1 {result.from} = {result.rate.toLocaleString(undefined, {
                minimumFractionDigits: 4,
                maximumFractionDigits: 6,
              })} {result.to} · {format(new Date(result.date + 'T00:00:00'), 'MMMM d, yyyy')}
            </p>
          </div>
        )}
      </div>

      <p className="mt-4 text-center text-xs text-muted-foreground">
        Data from{' '}
        <a
          href="https://www.frankfurter.app"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-foreground"
        >
          Frankfurter
        </a>{' '}
        · ECB reference rates · Updated daily
      </p>
    </div>
  )
}
